<template>
  <v-dialog v-model="internalVisible" :max-width="'80vw'">
    <v-card>
      <v-card-title class="headline d-flex justify-space-between align-center">
        新增假期申請
      </v-card-title>
      <v-card-text class="pa-6">
        <v-form ref="form" v-model="formValid">
          <v-text-field
            :value="companyDisplayText"
            label="公司"
            outlined
            dense
            readonly
          ></v-text-field>

          <!-- row 1 -->
          <v-row dense>
            <v-col cols="12" md="4">
              <v-autocomplete
                v-model="formData.userId"
                :items="userOptions"
                :loading="userLoading"
                :search-input.sync="userSearch"
                item-text="display"
                item-value="id"
                label="選擇員工"
                outlined
                dense
                hide-no-data
                clearable
                :rules="[(v) => !!v || '請選擇員工']"
                @focus="handleUserAutocompleteFocus"
              >
                <template v-slot:item="{ item }">
                  <div class="d-flex flex-column">
                    <span>{{ item.name }}</span>
                    <span class="text-caption grey--text">{{
                      item.email
                    }}</span>
                  </div>
                </template>
                <template v-slot:append-item>
                  <v-list-item
                    v-if="userHasMoreOptions"
                    dense
                    class="justify-center text-primary"
                    @click="loadMoreUserOptions"
                  >
                    <v-list-item-title>
                      {{ userLoading ? "載入中..." : "載入更多" }}
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    v-else-if="!userLoading && !userOptions.length"
                    dense
                  >
                    <v-list-item-title class="text-caption text-center">
                      無符合結果
                    </v-list-item-title>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="12" md="4">
              <v-select
                v-model="formData.applyType"
                :items="applyTypeOptions"
                label="申請類型"
                outlined
                dense
                :rules="[
                  (v) => (v !== null && v !== undefined) || '請選擇申請類型',
                ]"
                @change="onApplyTypeChange"
              ></v-select>
            </v-col>
            <v-col cols="12" md="4" v-if="formData.applyType === 1">
              <v-select
                v-model="formData.leaveType"
                :items="leaveTypeOptions"
                label="假別"
                outlined
                dense
                :rules="[
                  (v) => (v !== null && v !== undefined) || '請選擇假別',
                ]"
              ></v-select>
            </v-col>
          </v-row>

          <!-- row 2 -->
          <v-row dense>
            <v-col cols="12" md="4">
              <div class="text-body-2 font-weight-medium mb-1">
                開始日期時間
              </div>
              <date-time-picker
                :value="startDateTimeDisplay"
                :date.sync="formData.startDate"
                :time.sync="formData.startTime"
                label="開始日期時間"
                outlined
                dense
                :disabled="!formData.userId"
                :rules="[(v) => !!formData.startDate || '請選擇開始日期時間']"
                @confirm="handleStartDateTimeConfirm"
              />
            </v-col>
            <v-col cols="12" md="4">
              <div class="text-body-2 font-weight-medium mb-1">
                結束日期時間
              </div>
              <date-time-picker
                :value="endDateTimeDisplay"
                :date.sync="formData.endDate"
                :time.sync="formData.endTime"
                label="結束日期時間"
                outlined
                dense
                :disabled="!formData.userId"
                :rules="[(v) => !!formData.endDate || '請選擇結束日期時間']"
                @confirm="handleEndDateTimeConfirm"
              />
            </v-col>
            <!-- 請假時間 -->
            <v-col cols="12" md="4" class="d-flex align-center">
              <span class="text-subtitle-1 font-weight-bold primary--text">
                時間: {{ formattedLeaveDuration }}
              </span>
              <v-btn
                icon
                x-small
                color="primary"
                class="ml-2"
                @click="toggleDurationUnit"
                title="切換單位"
              >
                <v-icon x-small>mdi-swap-horizontal</v-icon>
              </v-btn>
            </v-col>
          </v-row>

          <!-- row 3 -->
          <v-row dense>
            <v-col cols="12" md="4">
              <v-autocomplete
                v-model="formData.reviewStaffId"
                :items="reviewStaffOptions"
                :loading="reviewStaffLoading"
                :search-input.sync="reviewStaffSearch"
                item-text="display"
                item-value="id"
                label="審核人"
                outlined
                dense
                hide-no-data
                clearable
                :rules="[(v) => !!v || '請選擇審核人']"
                @focus="handleReviewStaffAutocompleteFocus"
              >
                <template v-slot:item="{ item }">
                  <div class="d-flex flex-column">
                    <span>{{ item.name }}</span>
                    <span class="text-caption grey--text">{{
                      item.email
                    }}</span>
                  </div>
                </template>
                <template v-slot:append-item>
                  <v-list-item
                    v-if="reviewStaffHasMoreOptions"
                    dense
                    class="justify-center text-primary"
                    @click="loadMoreReviewStaffOptions"
                  >
                    <v-list-item-title>
                      {{ reviewStaffLoading ? "載入中..." : "載入更多" }}
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    v-else-if="!reviewStaffLoading && !reviewStaffOptions.length"
                    dense
                  >
                    <v-list-item-title class="text-caption text-center">
                      無符合結果
                    </v-list-item-title>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="12" md="4">
              <v-autocomplete
                v-model="formData.dutyAgentId"
                :items="dutyAgentOptions"
                :loading="dutyAgentLoading"
                :search-input.sync="dutyAgentSearch"
                item-text="display"
                item-value="id"
                label="代理人"
                outlined
                dense
                hide-no-data
                clearable
                @focus="handleDutyAgentAutocompleteFocus"
              >
                <template v-slot:item="{ item }">
                  <div class="d-flex flex-column">
                    <span>{{ item.name }}</span>
                    <span class="text-caption grey--text">{{
                      item.email
                    }}</span>
                  </div>
                </template>
                <template v-slot:append-item>
                  <v-list-item
                    v-if="dutyAgentHasMoreOptions"
                    dense
                    class="justify-center text-primary"
                    @click="loadMoreDutyAgentOptions"
                  >
                    <v-list-item-title>
                      {{ dutyAgentLoading ? "載入中..." : "載入更多" }}
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    v-else-if="!dutyAgentLoading && !dutyAgentOptions.length"
                    dense
                  >
                    <v-list-item-title class="text-caption text-center">
                      無符合結果
                    </v-list-item-title>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </v-col>

            <v-col
              cols="12"
              md="4"
              class="d-flex align-center"
              v-if="showOvertimeToggle"
            >
              <div>
                <div class="text-body-2 font-weight-medium mb-1">加班補償</div>
                <v-btn-toggle
                  v-model="rewardSelection"
                  :mandatory="overtimeRewardMode === 'either'"
                  class="w-100"
                  @change="onRewardSelectionChange"
                  active-class="primary blue--text"
                >
                  <v-btn
                    value="pay"
                    color="primary"
                    text
                    block
                    :disabled="rewardToggleDisabled"
                  >
                    加班費
                  </v-btn>
                  <v-btn
                    value="comp"
                    color="primary"
                    text
                    block
                    :disabled="rewardToggleDisabled"
                  >
                    換補休
                  </v-btn>
                </v-btn-toggle>
                <div class="text-caption grey--text mt-1">
                  {{ rewardHintText }}
                </div>
              </div>
            </v-col>
          </v-row>

          <!-- row 4 -->
          <v-row dense>
            <v-col cols="12" md="8">
              <v-textarea
                v-model="formData.reason"
                label="事由"
                solo
                dense
                rows="3"
                :rules="[(v) => !!v || '請輸入事由']"
              ></v-textarea>
            </v-col>
            <v-col cols="12" md="4">
              <v-file-input
                v-model="formData.attachments"
                label="附件（可多選）"
                prepend-icon="mdi-paperclip"
                multiple
                show-size
                outlined
                dense
                accept="*"
              ></v-file-input>
            </v-col>
          </v-row>

          <!-- row 5 -->
          <v-row dense v-if="showShiftFields">
            <v-col cols="12" md="4">
              <v-text-field
                v-model="formData.applyShiftId"
                label="申請班別 ID（apply_shift_id，可選）"
                outlined
                dense
                type="number"
                min="0"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="formData.targetStaffId"
                label="目標員工 ID（target_staff_id，可選）"
                outlined
                dense
                type="number"
                min="0"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="formData.targetShiftId"
                label="目標班別 ID（target_shift_id，可選）"
                outlined
                dense
              ></v-text-field>
            </v-col>
          </v-row>
          <!-- row 6 -->
          <v-row dense v-if="showLocationField">
            <v-col cols="12">
              <v-text-field
                v-model="formData.location"
                label="地點（location，可選）"
                outlined
                dense
              ></v-text-field>
            </v-col>
          </v-row>
          <!-- <v-textarea
            v-model="formData.extra"
            label="額外資訊（extra，可選）"
            outlined
            dense
            rows="2"
          ></v-textarea> -->
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          class="
            font-weight-bold
            text-capitalize
            btn-ls btn-secondary
            bg-gradient-light
            py-3
            px-6
          "
          @click="closeDialog"
          :disabled="submitting"
        >
          取消
        </v-btn>
        <v-btn
          class="
            font-weight-bold
            text-capitalize
            btn-primary
            bg-gradient-primary
            py-3
            px-6
          "
          @click="submitForm"
          :loading="submitting"
          :disabled="!formValid || submitting"
        >
          送出
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { LEAVE_TYPE, APPLY_TYPE } from "@/constants";
import { mapGetters } from "vuex";
import DateTimePicker from "@/components/Common/DateTimePicker.vue";
import {
  calculateScheduledWorkingMinutes,
  formatLeaveDurationText,
} from "@/utils/attendance";

export default {
  name: "CreateLeaveApplicationDialog",
  components: {
    DateTimePicker,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const today = `${year}-${month}-${day}`;

    return {
      formValid: false,
      submitting: false,
      formData: {
        applyType: null,
        leaveType: null,
        startDate: today,
        startTime: "09:00",
        endDate: today,
        endTime: "18:00",
        reason: "",
        dutyAgentId: null,
        reviewStaffId: null,
        isMakeupLeave: false,
        applyShiftId: "",
        targetStaffId: "",
        targetShiftId: "",
        location: "",
        extra: "",
        attachments: [],
        userId: null,
      },
      applyTypeOptions: Object.entries(APPLY_TYPE).map(([value, text]) => ({
        text,
        value: Number(value),
      })),
      userOptions: [],
      userLoading: false,
      userSearch: "",
      userSearchTimer: null,
      userHasMoreOptions: false,
      userOptionsPage: 1,
      userOptionsPageSize: 10,
      userOptionsLastSearch: "",
      dutyAgentOptions: [],
      dutyAgentLoading: false,
      dutyAgentSearch: "",
      dutyAgentSearchTimer: null,
      dutyAgentHasMoreOptions: false,
      dutyAgentOptionsPage: 1,
      dutyAgentOptionsPageSize: 10,
      dutyAgentOptionsLastSearch: "",
      reviewStaffOptions: [],
      reviewStaffLoading: false,
      reviewStaffSearch: "",
      reviewStaffSearchTimer: null,
      reviewStaffHasMoreOptions: false,
      reviewStaffOptionsPage: 1,
      reviewStaffOptionsPageSize: 10,
      reviewStaffOptionsLastSearch: "",
      overtimeRewardPolicy: null,
      overtimeRewardPolicyLoading: false,
      rewardSelection: null,
      configuringRewardUi: false,
      selectedUserClockSetting: null,
      calculatedLeaveDuration: 0,
      durationUnit: localStorage.getItem("leave_duration_unit") || "hours",
    };
  },
  computed: {
    ...mapGetters("user", ["userCompanyId"]),
    ...mapGetters("company", ["companies", "selectedCompany"]),
    internalVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit("update:visible", value);
      },
    },
    leaveTypeOptions() {
      return Object.entries(LEAVE_TYPE).map(([value, text]) => ({
        text,
        value: parseInt(value),
      }));
    },
    currentCompanyId() {
      if (this.selectedCompany && this.selectedCompany.id) {
        return this.selectedCompany.id;
      }
      return this.userCompanyId || null;
    },
    companyDisplayText() {
      if (this.selectedCompany && this.selectedCompany.company_name) {
        return `${this.selectedCompany.company_name}`;
      }
      if (this.currentCompanyId) {
        return `公司 ID: ${this.currentCompanyId}`;
      }
      return "尚未選擇公司";
    },
    startDateTimeDisplay() {
      return this.formatDateTimeDisplay(
        this.formData.startDate,
        this.formData.startTime
      );
    },
    endDateTimeDisplay() {
      return this.formatDateTimeDisplay(
        this.formData.endDate,
        this.formData.endTime
      );
    },
    showShiftFields() {
      return this.formData.applyType === 3;
    },
    showLocationField() {
      return this.formData.applyType === 5 || this.formData.applyType === 6;
    },
    showOvertimeToggle() {
      return this.formData.applyType === 2 && !!this.overtimeRewardPolicy;
    },
    overtimeRewardMode() {
      const policy = (this.overtimeRewardPolicy || "").toLowerCase();
      if (policy === "either") return "either";
      if (policy === "both") return "both";
      return "pay";
    },
    rewardToggleDisabled() {
      return this.overtimeRewardMode !== "either";
    },
    rewardHintText() {
      if (!this.showOvertimeToggle) return "";
      if (this.overtimeRewardMode === "either") {
        return "可自行選擇加班費或換補休";
      }
      if (this.overtimeRewardMode === "both") {
        return "公司將同時提供加班費與補休";
      }
      return "公司僅提供加班費";
    },
    formattedLeaveDuration() {
      if (!this.formData.startDate || !this.formData.endDate) return "";

      const simulatedRequest = {
        apply_type: this.formData.applyType,
        start_time: this.buildIsoString(
          this.formData.startDate,
          this.formData.startTime,
          false
        ),
        end_time: this.buildIsoString(
          this.formData.endDate,
          this.formData.endTime,
          true
        ),
        leave_minutes: this.calculatedLeaveDuration,
        applicant: {
          employeeClockSetting: this.selectedUserClockSetting,
        },
      };

      return formatLeaveDurationText(simulatedRequest, this.durationUnit);
    },
  },
  watch: {
    internalVisible(val) {
      if (val) {
        this.durationUnit =
          localStorage.getItem("leave_duration_unit") || "hours";
        if (!this.formData.reviewStaffId && this.userCompanyId) {
          this.formData.reviewStaffId = null;
        }
        this.resetUserOptionsPagination();
        this.fetchUserOptions(this.userSearch);
        this.resetDutyAgentOptionsPagination();
        this.resetReviewStaffOptionsPagination();
        this.ensureOvertimeRewardPolicyLoaded();
      } else {
        this.resetForm();
        this.userSearch = "";
        this.clearUserSearchTimer();
        this.resetUserOptionsPagination();
        this.resetDutyAgentOptionsPagination();
        this.resetReviewStaffOptionsPagination();
        this.dutyAgentSearch = "";
        this.reviewStaffSearch = "";
        this.clearDutyAgentSearchTimer();
        this.clearReviewStaffSearchTimer();
        this.overtimeRewardPolicy = null;
        this.rewardSelection = null;
      }
    },
    selectedCompany() {
      this.formData.userId = null;
      this.selectedUserClockSetting = null;
      this.calculatedLeaveDuration = 0;
      this.formData.dutyAgentId = null;
      this.formData.reviewStaffId = null;
      this.resetDutyAgentOptionsPagination();
      this.resetReviewStaffOptionsPagination();
      this.resetUserOptionsPagination();
      if (this.internalVisible) {
        this.fetchUserOptions(this.userSearch);
        this.ensureOvertimeRewardPolicyLoaded();
      }
    },
    "formData.userId"(val) {
      if (val) {
        this.fetchSelectedUserProfile(val);
      } else {
        this.selectedUserClockSetting = null;
        this.calculatedLeaveDuration = 0;
      }
    },
    "formData.startDate": "calculateRequestedMinutes",
    "formData.startTime": "calculateRequestedMinutes",
    "formData.endDate": "calculateRequestedMinutes",
    "formData.endTime": "calculateRequestedMinutes",
    selectedUserClockSetting(val) {
      if (val) {
        if (val.clock_in_time) {
          this.formData.startTime = val.clock_in_time.substring(0, 5);
        }
        if (val.clock_out_time) {
          this.formData.endTime = val.clock_out_time.substring(0, 5);
        }
      }
      this.calculateRequestedMinutes();
    },
    userSearch(val) {
      if (!this.internalVisible) return;
      this.scheduleUserSearch(val);
    },
    dutyAgentSearch(val) {
      if (!this.internalVisible) return;
      if (val === null || val === undefined) return;
      this.scheduleDutyAgentSearch(val);
    },
    reviewStaffSearch(val) {
      if (!this.internalVisible) return;
      if (val === null || val === undefined) return;
      this.scheduleReviewStaffSearch(val);
    },
    overtimeRewardPolicy() {
      this.configureRewardUi();
    },
    "formData.applyType"(val) {
      this.configureRewardUi();
      if (val === 10 || val === "10") {
        this.calculateRequestedMinutes();
      }
    },
  },
  mounted() {
    window.addEventListener(
      "leave-duration-unit-changed",
      this.syncDurationUnit
    );
  },
  beforeDestroy() {
    window.removeEventListener(
      "leave-duration-unit-changed",
      this.syncDurationUnit
    );
  },
  methods: {
    syncDurationUnit() {
      this.durationUnit =
        localStorage.getItem("leave_duration_unit") || "hours";
    },
    toggleDurationUnit() {
      this.durationUnit = this.durationUnit === "hours" ? "days" : "hours";
      localStorage.setItem("leave_duration_unit", this.durationUnit);
      window.dispatchEvent(new Event("leave-duration-unit-changed"));
    },
    // ... existing methods
    // Insert new calculation methods here
    async fetchSelectedUserProfile(userId) {
      if (!userId) return;
      try {
        const response = await this.$axios.get(`/api/users/${userId}`);
        this.selectedUserClockSetting =
          response.data?.employeeClockSetting || null;
      } catch (error) {
        console.error("Error fetching user profile for clock setting:", error);
        this.selectedUserClockSetting = null;
      }
    },
    async calculateRequestedMinutes() {
      this.calculatedLeaveDuration = 0;
      if (
        !this.formData.startDate ||
        !this.formData.endDate ||
        !this.formData.userId ||
        !this.currentCompanyId
      ) {
        return;
      }

      if (this.formData.applyType === 10 || this.formData.applyType === "10") {
        const start = new Date(this.formData.startDate);
        const end = new Date(this.formData.endDate);
        const days = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;

        let dayMinutes = 480;
        const clockSetting = this.selectedUserClockSetting;
        if (
          clockSetting &&
          clockSetting.clock_in_time &&
          clockSetting.clock_out_time
        ) {
          const d = new Date();
          d.setHours(0, 0, 0, 0);
          const dummyStart = new Date(d);
          const dummyEnd = new Date(d);
          dummyEnd.setHours(23, 59, 59, 999);
          const singleDayMins = calculateScheduledWorkingMinutes(
            dummyStart,
            dummyEnd,
            {
              ...clockSetting,
              fixed_working_days: true,
              recess_day: "",
              regular_day: "",
            }
          );
          if (singleDayMins > 0) {
            dayMinutes = singleDayMins;
          }
        }

        this.calculatedLeaveDuration = days * dayMinutes;
        return;
      }

      const clockSetting = this.selectedUserClockSetting;
      if (clockSetting) {
        const startCal = new Date(
          this.buildIsoString(
            this.formData.startDate,
            this.formData.startTime,
            false
          )
        );
        const endCal = new Date(
          this.buildIsoString(
            this.formData.endDate,
            this.formData.endTime,
            true
          )
        );

        const calculated = calculateScheduledWorkingMinutes(
          startCal,
          endCal,
          clockSetting,
          this.formData.applyType
        );
        this.calculatedLeaveDuration = calculated > 0 ? calculated : 0;
      } else {
        const startCal = new Date(
          this.buildIsoString(
            this.formData.startDate,
            this.formData.startTime,
            false
          )
        );
        const endCal = new Date(
          this.buildIsoString(
            this.formData.endDate,
            this.formData.endTime,
            true
          )
        );
        const diffValid = Math.floor((endCal - startCal) / 60000);
        this.calculatedLeaveDuration = diffValid > 0 ? diffValid : 0;
      }
    },
    // End new calculation methods

    onApplyTypeChange() {
      if (this.formData.applyType !== 1) {
        this.formData.leaveType = null;
      }
      if (this.formData.applyType !== 3) {
        this.formData.applyShiftId = "";
        this.formData.targetStaffId = "";
        this.formData.targetShiftId = "";
      }
      if (this.formData.applyType !== 5 && this.formData.applyType !== 6) {
        this.formData.location = "";
      }
      if (this.formData.applyType !== 2) {
        this.formData.isMakeupLeave = false;
      }
    },
    closeDialog() {
      this.internalVisible = false;
    },
    resetForm() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");
      const today = `${year}-${month}-${day}`;

      this.formData = {
        applyType: null,
        leaveType: null,
        startDate: today,
        startTime:
          this.selectedUserClockSetting?.clock_in_time?.substring(0, 5) ||
          "09:00",
        endDate: today,
        endTime:
          this.selectedUserClockSetting?.clock_out_time?.substring(0, 5) ||
          "18:00",
        reason: "",
        dutyAgentId: "",
        reviewStaffId: null,
        isMakeupLeave: false,
        applyShiftId: "",
        targetStaffId: "",
        targetShiftId: "",
        location: "",
        extra: "",
        attachments: [],
        userId: null,
      };
      this.selectedUserClockSetting = null;
      this.calculatedLeaveDuration = 0;
      this.overtimeRewardPolicy = null;
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
    onRewardSelectionChange(val) {
      if (this.configuringRewardUi) return;
      if (!this.showOvertimeToggle) return;
      this.formData.isMakeupLeave = val === "comp";
    },
    async ensureOvertimeRewardPolicyLoaded() {
      if (!this.currentCompanyId) {
        this.overtimeRewardPolicy = null;
        return;
      }
      const policyFromSelected =
        this.selectedCompany &&
        Object.prototype.hasOwnProperty.call(
          this.selectedCompany,
          "overtime_reward_policy"
        )
          ? this.selectedCompany.overtime_reward_policy
          : undefined;

      if (policyFromSelected !== undefined) {
        this.overtimeRewardPolicy = policyFromSelected;
        return;
      }

      this.overtimeRewardPolicyLoading = true;
      try {
        const response = await this.$axios.get(
          `/api/companies/${this.currentCompanyId}`
        );
        this.overtimeRewardPolicy =
          response?.data?.overtime_reward_policy || null;
      } catch (error) {
        console.error("Error fetching company overtime_reward_policy:", error);
        this.overtimeRewardPolicy = null;
      } finally {
        this.overtimeRewardPolicyLoading = false;
      }
    },
    configureRewardUi() {
      if (!this.showOvertimeToggle) {
        this.rewardSelection = null;
        return;
      }

      this.configuringRewardUi = true;

      if (this.overtimeRewardMode === "either") {
        const next = this.formData.isMakeupLeave ? "comp" : "pay";
        this.rewardSelection = next;
      } else if (this.overtimeRewardMode === "both") {
        this.rewardSelection = "pay";
        this.formData.isMakeupLeave = false;
      } else {
        this.rewardSelection = "pay";
        this.formData.isMakeupLeave = false;
      }

      this.configuringRewardUi = false;
    },
    buildRewardExtraJsonString() {
      const policy = (this.overtimeRewardPolicy || "").toLowerCase();
      const overtimeRewardPolicy =
        policy === "either" || policy === "both" ? policy : "pay";

      let rewardSelection = ["pay"];
      if (overtimeRewardPolicy === "both") {
        rewardSelection = ["pay", "comp"];
      } else if (overtimeRewardPolicy === "either") {
        rewardSelection = [this.rewardSelection === "comp" ? "comp" : "pay"];
      }

      return JSON.stringify({
        overtime_reward_policy: overtimeRewardPolicy,
        reward_selection: rewardSelection,
      });
    },
    clearUserSearchTimer() {
      if (this.userSearchTimer) {
        clearTimeout(this.userSearchTimer);
        this.userSearchTimer = null;
      }
    },
    scheduleUserSearch(term) {
      this.clearUserSearchTimer();
      this.userSearchTimer = setTimeout(() => {
        this.fetchUserOptions(term);
      }, 300);
    },
    clearDutyAgentSearchTimer() {
      if (this.dutyAgentSearchTimer) {
        clearTimeout(this.dutyAgentSearchTimer);
        this.dutyAgentSearchTimer = null;
      }
    },
    clearReviewStaffSearchTimer() {
      if (this.reviewStaffSearchTimer) {
        clearTimeout(this.reviewStaffSearchTimer);
        this.reviewStaffSearchTimer = null;
      }
    },
    scheduleDutyAgentSearch(term) {
      this.clearDutyAgentSearchTimer();
      this.dutyAgentSearchTimer = setTimeout(() => {
        this.fetchDutyAgentOptions(term);
      }, 300);
    },
    scheduleReviewStaffSearch(term) {
      this.clearReviewStaffSearchTimer();
      this.reviewStaffSearchTimer = setTimeout(() => {
        this.fetchReviewStaffOptions(term);
      }, 300);
    },
    async fetchUserOptions(searchTerm = "", page = 1, append = false) {
      if (!this.currentCompanyId) {
        if (!append) {
          this.userOptions = [];
          this.userHasMoreOptions = false;
          this.userOptionsPage = 1;
        }
        return;
      }

      const normalizedSearch = (searchTerm || "").toString().trim();
      const nextPage = Number(page) || 1;
      if (!append) {
        this.userOptionsPage = nextPage;
        this.userHasMoreOptions = false;
      }

      this.userLoading = true;
      try {
        const params = new URLSearchParams({
          page: String(nextPage),
          limit: String(this.userOptionsPageSize),
          is_active: "1",
          approval_status: "審核通過",
          company_id: String(this.currentCompanyId),
        });
        if (normalizedSearch) {
          params.append("name", normalizedSearch);
        }
        const response = await this.$axios.get(
          `/api/users?${params.toString()}`
        );
        const data = response?.data?.data || [];
        const mapped = data.map((user) => ({
          id: user.id,
          name: user.name || `使用者 #${user.id}`,
          email: user.email || "",
          display: user.name || `使用者 #${user.id}`,
        }));

        if (append) {
          const existingIds = new Set(this.userOptions.map((user) => user.id));
          const merged = [...this.userOptions];
          mapped.forEach((user) => {
            if (!existingIds.has(user.id)) {
              merged.push(user);
              existingIds.add(user.id);
            }
          });
          this.userOptions = merged;
        } else {
          this.userOptions = mapped;
        }

        const pagination = response?.data?.pagination;
        if (pagination) {
          this.userHasMoreOptions = Boolean(pagination.hasNextPage);
          this.userOptionsPage = pagination.currentPage || nextPage;
        } else {
          this.userHasMoreOptions = mapped.length === this.userOptionsPageSize;
          this.userOptionsPage = nextPage;
        }
        this.userOptionsLastSearch = normalizedSearch;
      } catch (error) {
        console.error("Error fetching user options:", error);
        if (!append) {
          this.userOptions = [];
          this.userHasMoreOptions = false;
        }
      } finally {
        this.userLoading = false;
      }
    },
    loadMoreUserOptions() {
      if (this.userLoading || !this.userHasMoreOptions) return;
      const nextPage = (this.userOptionsPage || 1) + 1;
      this.fetchUserOptions(
        this.userOptionsLastSearch,
        nextPage,
        true
      );
    },
    resetUserOptionsPagination() {
      this.userOptions = [];
      this.userHasMoreOptions = false;
      this.userOptionsPage = 1;
      this.userOptionsLastSearch = "";
    },
    handleUserAutocompleteFocus() {
      if (!this.userOptions.length) {
        this.fetchUserOptions(this.userSearch);
      }
    },
    handleDutyAgentAutocompleteFocus() {
      if (!this.dutyAgentOptions.length) {
        this.fetchDutyAgentOptions(this.dutyAgentSearch);
      }
    },
    handleReviewStaffAutocompleteFocus() {
      if (!this.reviewStaffOptions.length) {
        this.fetchReviewStaffOptions(this.reviewStaffSearch);
      }
    },
    async fetchDutyAgentOptions(searchTerm = "", page = 1, append = false) {
      const normalizedSearch = (searchTerm || "").toString().trim();
      const nextPage = Number(page) || 1;
      if (!append) {
        this.dutyAgentOptionsPage = nextPage;
        this.dutyAgentHasMoreOptions = false;
      }

      const { options, pagination } = await this.fetchCompanyUsersBySupervisor(
        0,
        normalizedSearch,
        nextPage,
        this.dutyAgentOptionsPageSize
      );

      if (append) {
        const existingIds = new Set(this.dutyAgentOptions.map((user) => user.id));
        const merged = [...this.dutyAgentOptions];
        options.forEach((user) => {
          if (!existingIds.has(user.id)) {
            merged.push(user);
            existingIds.add(user.id);
          }
        });
        this.dutyAgentOptions = merged;
      } else {
        this.dutyAgentOptions = options;
      }

      if (pagination) {
        this.dutyAgentHasMoreOptions = Boolean(pagination.hasNextPage);
        this.dutyAgentOptionsPage = pagination.currentPage || nextPage;
      } else {
        this.dutyAgentHasMoreOptions =
          options.length === this.dutyAgentOptionsPageSize;
        this.dutyAgentOptionsPage = nextPage;
      }
      this.dutyAgentOptionsLastSearch = normalizedSearch;
    },
    async fetchReviewStaffOptions(searchTerm = "", page = 1, append = false) {
      const normalizedSearch = (searchTerm || "").toString().trim();
      const nextPage = Number(page) || 1;
      if (!append) {
        this.reviewStaffOptionsPage = nextPage;
        this.reviewStaffHasMoreOptions = false;
      }

      const { options, pagination } = await this.fetchCompanyUsersBySupervisor(
        1,
        normalizedSearch,
        nextPage,
        this.reviewStaffOptionsPageSize
      );

      if (append) {
        const existingIds = new Set(this.reviewStaffOptions.map((user) => user.id));
        const merged = [...this.reviewStaffOptions];
        options.forEach((user) => {
          if (!existingIds.has(user.id)) {
            merged.push(user);
            existingIds.add(user.id);
          }
        });
        this.reviewStaffOptions = merged;
      } else {
        this.reviewStaffOptions = options;
      }

      if (pagination) {
        this.reviewStaffHasMoreOptions = Boolean(pagination.hasNextPage);
        this.reviewStaffOptionsPage = pagination.currentPage || nextPage;
      } else {
        this.reviewStaffHasMoreOptions =
          options.length === this.reviewStaffOptionsPageSize;
        this.reviewStaffOptionsPage = nextPage;
      }
      this.reviewStaffOptionsLastSearch = normalizedSearch;
    },
    loadMoreDutyAgentOptions() {
      if (this.dutyAgentLoading || !this.dutyAgentHasMoreOptions) return;
      const nextPage = (this.dutyAgentOptionsPage || 1) + 1;
      this.fetchDutyAgentOptions(this.dutyAgentOptionsLastSearch, nextPage, true);
    },
    loadMoreReviewStaffOptions() {
      if (this.reviewStaffLoading || !this.reviewStaffHasMoreOptions) return;
      const nextPage = (this.reviewStaffOptionsPage || 1) + 1;
      this.fetchReviewStaffOptions(
        this.reviewStaffOptionsLastSearch,
        nextPage,
        true
      );
    },
    resetDutyAgentOptionsPagination() {
      this.dutyAgentOptions = [];
      this.dutyAgentHasMoreOptions = false;
      this.dutyAgentOptionsPage = 1;
      this.dutyAgentOptionsLastSearch = "";
    },
    resetReviewStaffOptionsPagination() {
      this.reviewStaffOptions = [];
      this.reviewStaffHasMoreOptions = false;
      this.reviewStaffOptionsPage = 1;
      this.reviewStaffOptionsLastSearch = "";
    },
    async fetchCompanyUsersBySupervisor(
      supervisorFlag,
      searchTerm = "",
      page = 1,
      limit = 20
    ) {
      if (!this.currentCompanyId) {
        return { options: [], pagination: null };
      }
      const loadingKey =
        supervisorFlag === 1 ? "reviewStaffLoading" : "dutyAgentLoading";
      this[loadingKey] = true;
      try {
        const params = new URLSearchParams({
          page: String(page),
          limit: String(limit),
          is_active: "1",
          approval_status: "審核通過",
          company_id: String(this.currentCompanyId),
          supervisor: String(supervisorFlag),
        });
        if (searchTerm && searchTerm.trim()) {
          params.append("name", searchTerm.trim());
        }
        const response = await this.$axios.get(
          `/api/users/company-users?${params.toString()}`
        );
        const data = response?.data?.data || [];
        const options = data.map((user) => ({
          id: user.id,
          name: user.name || `使用者 #${user.id}`,
          email: user.email || "",
          display: user.name || `使用者 #${user.id}`,
        }));
        return {
          options,
          pagination: response?.data?.pagination || null,
        };
      } catch (error) {
        console.error("Error fetching supervisor users:", error);
        return { options: [], pagination: null };
      } finally {
        this[loadingKey] = false;
      }
    },
    formatDateTimeDisplay(date, time) {
      if (!date) return "";
      return time ? `${date} ${time}` : date;
    },
    handleStartDateTimeConfirm() {
      if (!this.formData.startTime) {
        this.formData.startTime = "09:00";
      }
    },
    handleEndDateTimeConfirm() {
      if (!this.formData.endTime) {
        this.formData.endTime = "18:00";
      }
    },
    buildIsoString(date, time, isEnd = false) {
      if (!date) return null;
      const safeTime = time || (isEnd ? "23:59" : "00:00");
      return new Date(`${date}T${safeTime}:00`).toISOString();
    },
    async submitForm() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.submitting = true;

      try {
        const formDataToSend = new FormData();
        formDataToSend.append("apply_type", this.formData.applyType);
        formDataToSend.append("client_source", "web");
        if (!this.currentCompanyId) {
          throw new Error("尚未選擇公司，無法送出申請");
        }
        if (!this.formData.userId) {
          throw new Error("請先選擇員工");
        }
        // NOTE 少入特別的參數：c_id(公司ID), u_id(員工ID)
        // c_id: 公司ID
        // u_id: 員工ID
        formDataToSend.append("c_id", this.currentCompanyId);
        formDataToSend.append("u_id", this.formData.userId);

        if (this.formData.applyType === 1 && this.formData.leaveType) {
          formDataToSend.append("leave_type", this.formData.leaveType);
        }

        const startTimeIsoUtc = this.buildIsoString(
          this.formData.startDate,
          this.formData.startTime,
          false
        );

        const endTimeIsoUtc = this.buildIsoString(
          this.formData.endDate,
          this.formData.endTime,
          true
        );

        formDataToSend.append("start_time", startTimeIsoUtc);
        formDataToSend.append("end_time", endTimeIsoUtc);
        formDataToSend.append("leave_minutes", this.calculatedLeaveDuration);

        formDataToSend.append("reason", this.formData.reason);
        formDataToSend.append(
          "duty_agent_id",
          this.formData.dutyAgentId ? String(this.formData.dutyAgentId) : ""
        );
        const reviewStaffId =
          this.formData.reviewStaffId || this.userCompanyId || "";
        formDataToSend.append("review_staff_id", reviewStaffId);
        formDataToSend.append(
          "is_makeup_leave",
          this.formData.isMakeupLeave ? "1" : "0"
        );

        if (this.formData.applyShiftId) {
          formDataToSend.append(
            "apply_shift_id",
            String(this.formData.applyShiftId)
          );
        }
        if (this.formData.targetStaffId) {
          formDataToSend.append(
            "target_staff_id",
            String(this.formData.targetStaffId)
          );
        }
        if (this.formData.targetShiftId) {
          formDataToSend.append("target_shift_id", this.formData.targetShiftId);
        }
        if (this.formData.location) {
          formDataToSend.append("location", this.formData.location);
        }

        if (this.formData.applyType === 2 && this.overtimeRewardPolicy) {
          formDataToSend.append("extra", this.buildRewardExtraJsonString());
        } else if (this.formData.extra) {
          formDataToSend.append("extra", this.formData.extra);
        }

        if (Array.isArray(this.formData.attachments)) {
          this.formData.attachments.forEach((file) => {
            if (file) {
              formDataToSend.append("attachments", file);
            }
          });
        }

        const response = await this.$axios.post(
          "/api/applications",
          formDataToSend,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        this.$emit("success", response.data);
        this.closeDialog();
      } catch (error) {
        console.error("Error creating application:", error);
        this.$emit("error", {
          title: "新增失敗",
          message:
            error.response?.data?.message ||
            "新增假期申請時發生錯誤，請稍後再試。",
        });
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
